import { Link } from "react-router-dom";
import {
  ArrowRight,
  Store,
  Globe,
  Bike,
  Cpu,
  ClipboardList,
  TrendingUp,
  Layers,
  MessageCircle,
} from "lucide-react";
import { SERVICES, whatsappUrl } from "@/lib/site-data";
import useSeo from "@/lib/useSeo";
import JsonLd from "@/lib/JsonLd";
import { SERVICE_META, ServiceIllustration } from "@/lib/service-visuals";

/* ------------------------------------------------------------------ *
 * Hero-side service network SVG — desktop only                        *
 * Nodes: Pazaryeri → Sipariş → Entegrasyon → Operasyon → Kurye → Teslimat
 * ------------------------------------------------------------------ */
function HeroNetwork() {
  const nodes = [
    { x: 55, y: 60, label: "Pazaryeri", Icon: Store },
    { x: 165, y: 40, label: "Sipariş", Icon: Globe },
    { x: 275, y: 80, label: "Entegrasyon", Icon: Cpu },
    { x: 165, y: 150, label: "Operasyon", Icon: TrendingUp },
    { x: 55, y: 210, label: "Kurye", Icon: Bike },
    { x: 275, y: 210, label: "Teslimat", Icon: ClipboardList },
  ];
  const edges = [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 4],
    [3, 5],
    [4, 5],
    [0, 3],
  ];
  const R = 30;

  return (
    <div
      className="ep-svc-network hidden lg:block"
      aria-hidden="true"
      data-testid="services-hero-network"
    >
      <svg viewBox="0 0 340 260" className="w-full h-auto">
        <defs>
          <radialGradient id="epNodeGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(0,242,46,0.35)" />
            <stop offset="100%" stopColor="rgba(0,242,46,0)" />
          </radialGradient>
          <linearGradient id="epLine" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(0,242,46,0)" />
            <stop offset="50%" stopColor="rgba(0,242,46,0.75)" />
            <stop offset="100%" stopColor="rgba(0,242,46,0)" />
          </linearGradient>
        </defs>
        {/* thin neon lines */}
        {edges.map(([a, b], i) => (
          <line
            key={i}
            x1={nodes[a].x}
            y1={nodes[a].y}
            x2={nodes[b].x}
            y2={nodes[b].y}
            stroke="url(#epLine)"
            strokeWidth="1.5"
            strokeLinecap="round"
            className={`ep-svc-edge ep-svc-edge-${i}`}
          />
        ))}
        {/* nodes */}
        {nodes.map((n, i) => (
          <g
            key={i}
            transform={`translate(${n.x}, ${n.y})`}
            className={`ep-svc-node ep-svc-node-${i}`}
          >
            <circle r={R + 12} fill="url(#epNodeGlow)" />
            <circle r={R} fill="#0d100d" stroke="rgba(0,242,46,0.55)" strokeWidth="1.5" />
            <foreignObject x={-R} y={-R} width={R * 2} height={R * 2}>
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#00F22E",
                }}
              >
                <n.Icon size={22} />
              </div>
            </foreignObject>
            <text
              y={R + 18}
              textAnchor="middle"
              fontFamily="Inter, system-ui, sans-serif"
              fontSize="10"
              fill="rgba(255,255,255,0.72)"
              letterSpacing="0.5"
            >
              {n.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Connected-solution stages (4 nodes)                                *
 * ------------------------------------------------------------------ */
const STAGES = [
  { title: "Satış Kanalı", desc: "Pazaryerleri + kendi sipariş sisteminiz", Icon: Store },
  { title: "Sipariş ve Ürün Yönetimi", desc: "Ürün, fiyat, stok ve menü", Icon: ClipboardList },
  { title: "Operasyon", desc: "Sipariş akışı ve kampanyalar", Icon: TrendingUp },
  { title: "Teslimat", desc: "Kurye çözümü ile son adım", Icon: Bike },
];

export default function Services() {
  useSeo({
    title: "Epersonel Hizmetleri | Dijital Satış Çözümleri",
    description:
      "Pazaryeri kurulumu, web ve mobil sipariş sistemi, kurye çözümü, fiyat-stok entegrasyonu ve dijital operasyon yönetimi — tüm Epersonel hizmetleri.",
  });

  return (
    <>
      <JsonLd
        id="services-breadcrumb"
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://epersonel.biz/" },
            { "@type": "ListItem", position: 2, name: "Hizmetlerimiz", item: "https://epersonel.biz/hizmetlerimiz" },
          ],
        }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-14 md:pt-20 pb-10 md:pb-16">
        <div className="ep-glow-tl" aria-hidden="true" />
        <div className="ep-container relative grid lg:grid-cols-[minmax(0,1fr)_400px] gap-10 lg:gap-14 items-center">
          <div className="max-w-2xl">
            <p className="text-xs tracking-[0.3em] text-[#00F22E] font-semibold mb-4">
              HİZMETLERİMİZ
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              İşyerinize Uçtan Uca{" "}
              <span className="text-[#00F22E]">Dijital Satış Çözümleri.</span>
            </h1>
            <p className="mt-6 text-white/85 text-base md:text-lg leading-relaxed max-w-xl">
              Pazaryeri kurulumundan kurye çözümüne kadar, dijital operasyonunuzun
              her adımında sizin ekibiniz gibi çalışırız.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/basvuru"
                data-testid="services-hero-cta-primary"
                className="btn-primary"
              >
                Ücretsiz Analiz İste <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="#services-grid"
                data-testid="services-hero-cta-secondary"
                className="btn-secondary"
              >
                Hizmetleri Keşfet
              </a>
            </div>
          </div>
          <HeroNetwork />
        </div>
      </section>

      {/* SERVICE CARDS */}
      <section
        id="services-grid"
        className="py-12 md:py-16 ep-section-tier-2 border-y border-white/[0.05] relative overflow-hidden scroll-mt-24"
      >
        <div className="ep-glow-br" aria-hidden="true" />
        <div className="ep-container relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {SERVICES.map((svc, i) => {
              const meta = SERVICE_META[svc.slug] || {
                Icon: Layers,
                category: "HİZMET",
                accent: "#00F22E",
              };
              const Icon = meta.Icon;
              return (
                <Link
                  key={svc.slug}
                  to={`/hizmetlerimiz/${svc.slug}`}
                  data-testid={`services-card-${svc.slug}`}
                  className="ep-svc-card group flex flex-col h-full"
                  style={{ "--svc-accent": meta.accent, animationDelay: `${i * 60}ms` }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-[#00F22E]/10 text-[#00F22E] grid place-items-center shrink-0 ring-1 ring-[#00F22E]/25">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="text-[10px] tracking-[0.24em] text-[#00F22E]/80 font-semibold">
                      {meta.category}
                    </span>
                  </div>
                  <h3 className="font-display text-lg md:text-xl font-semibold tracking-tight leading-snug">
                    {svc.title}
                  </h3>
                  <p className="mt-3 text-sm text-white/70 leading-relaxed flex-1">
                    {svc.short}
                  </p>
                  <ServiceIllustration slug={svc.slug} />
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#00F22E] font-medium group-hover:gap-2.5 transition-[gap] duration-200">
                    {svc.ctaLabel} <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONNECTED SOLUTION */}
      <section className="py-16 md:py-20 relative overflow-hidden">
        <div className="ep-container relative">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Tek Tek Hizmet Değil,{" "}
              <span className="text-[#00F22E]">Birbirine Bağlı Bir Sistem.</span>
            </h2>
            <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed">
              İhtiyacınıza göre tek bir çözümle başlayabilir veya pazaryeri
              yönetiminden sipariş sistemine, entegrasyondan kurye operasyonuna
              kadar tüm süreci Epersonel ile birlikte yönetebilirsiniz.
            </p>
          </div>
          <div className="mt-12 ep-stage-row" data-testid="connected-stages">
            {STAGES.map((s, i) => {
              const Icon = s.Icon;
              return (
                <div key={i} className="ep-stage" style={{ animationDelay: `${i * 90}ms` }}>
                  <div className="ep-stage-node">
                    <span className="ep-stage-index">0{i + 1}</span>
                    <div className="ep-stage-icon">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="ep-stage-title">{s.title}</h3>
                    <p className="ep-stage-desc">{s.desc}</p>
                  </div>
                  {i < STAGES.length - 1 && (
                    <span className="ep-stage-connector" aria-hidden="true" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DECISION CTA */}
      <section className="py-14 md:py-16 ep-section-tier-2 border-y border-white/[0.05] relative overflow-hidden">
        <div className="ep-glow-tl" aria-hidden="true" />
        <div className="ep-glow-br" aria-hidden="true" />
        <div className="ep-container relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Hangi Çözümün İşyerinize Uygun Olduğunu{" "}
              <span className="text-[#00F22E]">Birlikte Belirleyelim.</span>
            </h2>
            <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed">
              Mevcut satış kanallarınızı, ürün yapınızı ve operasyon ihtiyacınızı
              inceleyerek işyerinize özel bir yol haritası oluşturalım.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/basvuru"
                data-testid="services-decision-cta-primary"
                className="btn-primary"
              >
                Ücretsiz Analiz İste <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={whatsappUrl()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="services-decision-cta-whatsapp"
                className="btn-secondary"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp&apos;tan Görüşelim
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
